// src/context/EventContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';
import {
  calculateBestMark,
  assignPlaces,
  parseTime,
  formatTime,
  calculateFinalists
} from '../utils/ResultUtils';
import { useBatchedWrites } from '../hooks/useBatchedWrites';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  getDocs,
  Timestamp,
  deleteDoc,
  onSnapshot,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { storage } from '../firebase';  
import { formatWithPrecision } from '../utils/precision';
import { throttle } from 'lodash';
import { ref as storageRef, listAll, getDownloadURL } from 'firebase/storage';

// --- Códigos de eventos ---
export const TrackEventCodes = [
  '10','15','20','25','30','35','40','45','50','55','60','60H','70','80','80H','100','100H',
  '110H','120','130','150','180','200','220','250','300','300H','350','400','400H',
  '400W','500','600','600W','700','800','800W','900','1000','1000W','1100','1200','1200W',
  '1300','1400','1500','1500W','1600','1600W','2000','2000S','2500','2000W','1MILE','3000',
  '3000S','3000W','3200','3200W','4000','5000','5K','5000W','6000','6K','7000','8000','8K',
  '9000','10K','10000W','10000','12K','15K','20K','16K','20000W','35000W','50000W','21K','42K',
  'HMAR','MAR'
];
export const fieldHorizontalEvents = [
  'SP1','DT1','HT1','LJ1','TJ1','JT1','WT1','SWT1'
];
export const fieldVerticalEvents = ['HJ1','PV1'];

const EventContext = createContext();

const nowTS = () => Timestamp.fromDate(new Date());

const makeKey = (eventNumber, round, heat) =>
  `${eventNumber}_${round}_${heat}`;

/** Convierte el formato nuevo (array + objeto attempts)
 *  al formato antiguo que usa el algoritmo de rankings         */
const buildHeightMap = (heightsArray = [], athleteDoc = {}) => {
  const attempts = athleteDoc.attempts || {};
  const map = {};
  heightsArray.forEach((h, idx) => {
    map[h.value.toFixed(2)] = { attempts: attempts[idx] || ['', '', ''] };
  });
  return map;
};

// Normaliza atleta
const processAthlete = athlete => {
  if (typeof athlete.idNumber === 'number') athlete.idNumber = athlete.idNumber.toString();
  if (typeof athlete.idNumber !== 'string') athlete.idNumber = '';
  if (typeof athlete.fullName !== 'string') {
    athlete.fullName = athlete.firstName && athlete.lastName
      ? `${athlete.firstName} ${athlete.lastName}`
      : 'Nombre Desconocido';
  }
  if (typeof athlete.affiliation !== 'string') athlete.affiliation = 'Sin Afiliación';
  return athlete;
};

export const EventProvider = ({ children, competitionId }) => {
  const { user } = useAuth();
  const userId = user?.uid || null; 
  
  // *** SOLUCIÓN 1: REF PARA COMPETENCIA ACTUAL ***
  const currentCompetitionRef = useRef(competitionId);
  const isChangingCompetition = useRef(false);
  
  // — Estados de datos —
  const [evtData, setEvtData] = useState([]);
  const [pplData, setPplData] = useState([]);
  const [schData, setSchData] = useState([]);
  const [participantsResults, setParticipantsResults] = useState({});
  const [rankings, setRankings] = useState({});

  // — Metadatos brutos de syncedFiles —
  const [fileMetadata, setFileMetadata] = useState({});

  // — UI & control —
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualUpload, setManualUpload] = useState(false);
  // Batching de escrituras
  const { queueWrite, flush } = useBatchedWrites(800);
  const [precision, setPrecision] = useState('1/100'); 
  const [rowPrecisions, setRowPrecisions] = useState({}); 
  const [testSettingsMap, setTestSettingsMap] = useState({});
  const [boardConfigs, setBoardConfigs] = useState([]);
  const [openWindows, setOpenWindows] = useState({});
  const [heatHeights, setHeatHeights] = useState({});   // { heatKey: array }

  const isCalculating = useRef(false);

  // *** SOLUCIÓN 2: FUNCIÓN PARA LIMPIAR TODOS LOS ESTADOS ***
  const clearAllStates = useCallback(() => {
    console.log(`🧹 Limpiando estados para competencia: ${currentCompetitionRef.current}`);
    setEvtData([]);
    setPplData([]);
    setSchData([]);
    setParticipantsResults({});
    setRankings({});
    setFileMetadata({});
    setPrecision('1/100');
    setRowPrecisions({});
    setTestSettingsMap({});
    setBoardConfigs([]);
    setError(null);
    isCalculating.current = false;
    // Limpiar cualquier escritura pendiente
    flush();
  }, [flush]);

  // *** SOLUCIÓN 3: DETECTAR CAMBIO DE COMPETENCIA Y LIMPIAR ***
  useEffect(() => {
    const previousCompetition = currentCompetitionRef.current;
    const newCompetition = competitionId;
    
    if (previousCompetition && previousCompetition !== newCompetition) {
      console.log(`🔄 Cambio de competencia detectado: ${previousCompetition} → ${newCompetition}`);
      isChangingCompetition.current = true;
      clearAllStates();
      
      // Pequeño delay para asegurar limpieza completa
      setTimeout(() => {
        isChangingCompetition.current = false;
        currentCompetitionRef.current = newCompetition;
      }, 100);
    } else {
      currentCompetitionRef.current = newCompetition;
    }
  }, [competitionId, clearAllStates]);

  // *** SOLUCIÓN 4: HELPER PARA VERIFICAR COMPETENCIA VÁLIDA ***
  const isValidCompetition = useCallback((compId) => {
    return compId && compId === currentCompetitionRef.current && !isChangingCompetition.current;
  }, []);

  const getTestSettings = useCallback((en, ro) => {
    const key = `${en}_${ro}`;
    return {
      numberOfAttempts: 0,
      numberOfPreliminaryAttempts: 0,
      hasFinalRound: false,
      athletesInFinal: 0,
      enableWind: false,
      enablePoints: false,
      lanesCount: 8,
      advancementType: '',
      advancementDetail: '',
      ...(testSettingsMap[key] || {})
    };
  }, [testSettingsMap]);

  const updatePrecision = useCallback(newPrecision => {
    if (!isValidCompetition(competitionId)) return;
    
    setPrecision(newPrecision);
    const refDoc = doc(db, 'competencias', competitionId);
    queueWrite(refDoc, { precision: newPrecision }, { merge: true });
  }, [competitionId, queueWrite, isValidCompetition]);

  // *** SOLUCIÓN 5: CARGA INICIAL CON VALIDACIÓN DE COMPETENCIA ***
  useEffect(() => {
    if (!competitionId?.trim() || isChangingCompetition.current) {
      setError("ID de competencia inválido.");
      setLoading(false);
      return;
    }

    (async () => {
      // Verificar que sigue siendo la competencia correcta antes de empezar
      if (!isValidCompetition(competitionId)) {
        console.log('🚫 Carga inicial cancelada - competencia ya no es válida');
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        console.log(`📊 Cargando datos iniciales para competencia: ${competitionId}`);
        
        const compRef = doc(db, 'competencias', competitionId);
        const [cSnap, eSnap, pSnap, sSnap] = await Promise.all([
          getDoc(compRef),
          getDoc(doc(collection(db, 'competencias', competitionId, 'evt'), 'data')),
          getDoc(doc(collection(db, 'competencias', competitionId, 'ppl'), 'data')),
          getDoc(doc(collection(db, 'competencias', competitionId, 'sch'), 'data'))
        ]);

        // *** VERIFICAR NUEVAMENTE ANTES DE APLICAR DATOS ***
        if (!isValidCompetition(competitionId)) {
          console.log('🚫 Carga inicial cancelada - competencia cambió durante la carga');
          return;
        }

        // Resultados y rankings
        if (cSnap.exists()) {
          const cd = cSnap.data();
          if (cd.participantsResults && Object.keys(cd.participantsResults).length > 0) {
            setParticipantsResults(cd.participantsResults);
          }
          if (cd.rankings && Object.keys(cd.rankings).length > 0) {
            setRankings(cd.rankings);
          }
          if (cd.precision) {
            setPrecision(cd.precision);
          }
          if (cd.rowPrecisions) {
            setRowPrecisions(cd.rowPrecisions);
          }
        }

        // Eventos
        if (eSnap.exists()) {
          const arr = eSnap.data().evtData || [];
          setEvtData(arr);
        } else {
          setEvtData([]);
        }

        // Atletas
        if (pSnap.exists()) {
          setPplData((pSnap.data().pplData || []).map(processAthlete));
        } else {
          setPplData([]);
        }

        // Horarios
        if (sSnap.exists()) {
          setSchData(sSnap.data().schData || []);
        } else {
          setSchData([]);
        }

        console.log(`✅ Datos iniciales cargados para competencia: ${competitionId}`);
        
      } catch (e) {
        console.error("Error carga inicial:", e);
        if (isValidCompetition(competitionId)) {
          setError(`Error al cargar datos: ${e.message}`);
          setEvtData([]); setPplData([]); setSchData([]);
          setParticipantsResults({}); setRankings({});
        }
      } finally {
        if (isValidCompetition(competitionId)) {
          setLoading(false);
        }
      }
    })();
  }, [competitionId, isValidCompetition]);

  // Forzar flush on unmount
  useEffect(() => () => { flush(); }, [flush]);

  // *** SOLUCIÓN 6: LISTENERS CON VALIDACIÓN DE COMPETENCIA ***
  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;
    
    console.log(`👂 Configurando listeners para competencia: ${competitionId}`);
    
    const colRef = collection(db, 'competencias', competitionId, 'syncedFiles');
    const unsub = onSnapshot(colRef, snapshot => {
      // *** VERIFICAR COMPETENCIA ANTES DE PROCESAR ***
      if (!isValidCompetition(competitionId)) {
        console.log('🚫 Listener syncedFiles ignorado - competencia ya no es válida');
        return;
      }
      
      const meta = {};
      snapshot.forEach(docSnap => {
        const d = docSnap.data();
        if (!d.url || !d.name || !d.timestamp) return;

        let key = docSnap.id;
        if (d.isFieldEventFile) {
          const lower = d.name.toLowerCase();
          if (lower.endsWith('.evt')) key = 'fldlynx_evt';
          else if (lower.endsWith('.sch')) key = 'fldlynx_sch';
        }

        meta[key] = {
          url: d.url,
          name: d.name,
          timestamp: d.timestamp.toDate(),
          originalPath: d.originalPath || '',
          type: d.type || '',
          isFieldEventFile: !!d.isFieldEventFile
        };
      });

      setFileMetadata(meta);
    });
    
    return () => {
      console.log(`🔌 Desconectando listener syncedFiles para: ${competitionId}`);
      unsub();
    };
  }, [competitionId, isValidCompetition]);

  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;
    
    const compRef = doc(db, 'competencias', competitionId);
    const unsub = onSnapshot(compRef, snap => {
      if (!isValidCompetition(competitionId)) return;
      
      const data = snap.data() || {};
      if (data.rowPrecisions) setRowPrecisions(data.rowPrecisions);
    });
    return () => unsub();
  }, [competitionId, isValidCompetition]);

  const refreshFiles = useCallback(async () => {
    if (!competitionId || !isValidCompetition(competitionId)) return;
    
    try {
      const snaps = await getDocs(collection(db, 'competencias', competitionId, 'syncedFiles'));
      
      if (!isValidCompetition(competitionId)) return; // Verificar después de la consulta async
      
      const meta = {};
      snaps.forEach(s => {
        const d = s.data();
        if (!d.url || !d.name || !d.timestamp) return;
        let key = s.id;
        if (d.isFieldEventFile) {
          const lower = d.name.toLowerCase();
          if (lower.endsWith('.evt')) key = 'fldlynx_evt';
          else if (lower.endsWith('.sch')) key = 'fldlynx_sch';
        }
        meta[key] = {
          url: d.url,
          name: d.name,
          timestamp: d.timestamp.toDate(),
          originalPath: d.originalPath || '',
          type: d.type || '',
          isFieldEventFile: !!d.isFieldEventFile
        };
      });
      setFileMetadata(meta);
    } catch (e) {
      console.error("Error refrescando archivos:", e);
      if (isValidCompetition(competitionId)) {
        setError(`Error refrescando archivos: ${e.message}`);
      }
    }
  }, [competitionId, isValidCompetition]);

  // *** SOLUCIÓN 7: GUARDAR DATOS CON VALIDACIÓN ***
  const saveDataToSubcollection = useCallback(async (subcol, key, arr) => {
    if (!competitionId || loading || !isValidCompetition(competitionId)) {
      console.log(`🚫 Guardado cancelado para ${subcol} - competencia no válida`);
      return;
    }
    
    if (!Array.isArray(arr)) {
      setError(`Err interno: ${subcol}`); 
      return;
    }
    
    try {
      console.log(`💾 Guardando ${subcol} para competencia: ${competitionId}`);
      await setDoc(
        doc(collection(db, 'competencias', competitionId, subcol), 'data'),
        { [key]: arr },
        { merge: true }
      );
    } catch (e) {
      console.error(`Error guardando ${subcol}:`, e);
      if (isValidCompetition(competitionId)) {
        setError(`Err guardando ${key}: ${e.message}`);
      }
    }
  }, [competitionId, loading, isValidCompetition]);

  // *** GUARDAR EVT/PPL/SCH CON VALIDACIÓN ***
  useEffect(() => {
    if (!loading && competitionId && isValidCompetition(competitionId)) {
      saveDataToSubcollection('evt','evtData',evtData);
    }
  }, [evtData, competitionId, loading, saveDataToSubcollection, isValidCompetition]);

  useEffect(() => {
    if (!loading && competitionId && isValidCompetition(competitionId)) {
      saveDataToSubcollection('ppl','pplData',pplData);
    }
  }, [pplData, competitionId, loading, saveDataToSubcollection, isValidCompetition]);

  useEffect(() => {
    if (!loading && competitionId && isValidCompetition(competitionId)) {
      saveDataToSubcollection('sch','schData',schData);
    }
  }, [schData, competitionId, loading, saveDataToSubcollection, isValidCompetition]);

  const saveDataToMainDoc = useCallback(async rawData => {
    if (!competitionId || loading || !isValidCompetition(competitionId)) return;
    
    const refDoc = doc(db, 'competencias', competitionId);
    const data = JSON.parse(JSON.stringify(rawData));
    try {
      await updateDoc(refDoc, data);
    } catch (err) {
      console.error("Error mainDoc:", err);
      if (isValidCompetition(competitionId)) {
        setError(`Err DB: ${err.message}`);
      }
    }
  }, [competitionId, loading, isValidCompetition]);

  const clearQualifiers = useCallback(async (en, ro) => {
    if (!isValidCompetition(competitionId)) return false;
  
    const updatePromises = [];
  
    setParticipantsResults(prev => {
      const next = { ...prev };
  
      Object.keys(prev).forEach(key => {
        const [ev, rnd] = key.split('_').map(Number);
        if (ev !== +en || rnd !== +ro) return;
  
        const heatMap   = { ...prev[key] };
        const fsPayload = {};
  
        Object.entries(heatMap).forEach(([pid, data]) => {
          /* ―――――――― nuevo ――――――――
             saltamos metadatos (status, __meta, etc.)
          */
          if (isNaN(+pid) || typeof data !== 'object' || !data) return;
  
          if ('qualifier' in data) {
            const { qualifier, ...rest } = data;
            heatMap[pid] = rest;                    // estado local
            fsPayload[`${pid}.qualifier`] = deleteField(); // Firestore
          }
        });
  
        next[key] = heatMap;
  
        if (Object.keys(fsPayload).length) {
          const ref = doc(
            db,
            'competencias',
            competitionId,
            'resultados',
            key
          );
          updatePromises.push(
            updateDoc(ref, fsPayload).catch(err => {
              console.error('⚠️ clearQualifiers:', err);
              throw err;
            })
          );
        }
      });
  
      return next;
    });
  
    /* Esperamos todas las escrituras */
    if (updatePromises.length) {
      try {
        await Promise.all(updatePromises);
      } catch (err) {
        console.error('Error al limpiar calificadores:', err);
        throw err;
      }
    }
  
    return true;
  }, [competitionId, isValidCompetition]);

  // *** LISTENERS DE EVT/PPL/SCH CON VALIDACIÓN ***
  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;

    console.log(`👂 Configurando listeners evt/ppl/sch para: ${competitionId}`);

    const unsubEvt = onSnapshot(
      doc(collection(db,'competencias',competitionId,'evt'),'data'),
      snap => {
        if (!isValidCompetition(competitionId)) return;
        if (snap.exists()) setEvtData(snap.data().evtData || []);
      }
    );
    
    const unsubPpl = onSnapshot(
      doc(collection(db,'competencias',competitionId,'ppl'),'data'),
      snap => {
        if (!isValidCompetition(competitionId)) return;
        if (snap.exists()) setPplData(snap.data().pplData || []);
      }
    );
    
    const unsubSch = onSnapshot(
      doc(collection(db,'competencias',competitionId,'sch'),'data'),
      snap => {
        if (!isValidCompetition(competitionId)) return;
        if (snap.exists()) setSchData(snap.data().schData || []);
      }
    );

    return () => { 
      console.log(`🔌 Desconectando listeners evt/ppl/sch para: ${competitionId}`);
      unsubEvt(); unsubPpl(); unsubSch(); 
    };
  }, [competitionId, isValidCompetition]);

  // *** LISTENER DE RESULTADOS CON VALIDACIÓN ***
  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;
    
    console.log(`👂 Configurando listener resultados para: ${competitionId}`);
    
    const colRef = collection(db, 'competencias', competitionId, 'resultados');
    const unsub = onSnapshot(colRef, snapshot => {
      if (!isValidCompetition(competitionId)) {
        console.log('🚫 Listener resultados ignorado - competencia ya no es válida');
        return;
      }
      
      console.log(`🔥 Snapshot de resultados para ${competitionId}:`, snapshot.docs.map(d => d.id));
      const pr = {};
      const hh = {};  
      snapshot.forEach(docSnap => {
        const data = docSnap.data() || {};
        pr[docSnap.id] = data;
      
        // si el doc tiene heights los copiamos
        if (data.heights) hh[docSnap.id] = data.heights;
      });
      setParticipantsResults(pr);
      setHeatHeights(hh);
    });
    
    return () => {
      console.log(`🔌 Desconectando listener resultados para: ${competitionId}`);
      unsub();
    };
  }, [competitionId, isValidCompetition]);

  useEffect(() => {
    if (!evtData.length || isCalculating.current || !isValidCompetition(competitionId)) return;
    
    isCalculating.current = true;
    
    const keys = Object.keys(participantsResults);
    const updates = keys.map(key => {
      const [en, ro, he] = key.split('_').map(Number);
      return calculateRankings(en, ro, he);
    });

    Promise.all(updates).finally(() => {
      isCalculating.current = false;
    });
  }, [participantsResults, evtData, competitionId, isValidCompetition]);

  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;
    
    const colRef = collection(db, 'competencias', competitionId, 'testSettings');
    const unsub = onSnapshot(colRef, snap => {
      if (!isValidCompetition(competitionId)) return;
      
      const map = {};
      snap.forEach(d => {
        const [ev, rnd] = d.id.split('_').map(Number);
        map[`${ev}_${rnd}`] = d.data();
      });
      setTestSettingsMap(map);
    });

    return () => unsub();
  }, [competitionId, isValidCompetition]);

  useEffect(() => {
    if (!competitionId || isChangingCompetition.current) return;
    
    const colRef = collection(db, 'competencias', competitionId, 'tableros');
    const unsub = onSnapshot(colRef, snapshot => {
      if (!isValidCompetition(competitionId)) return;
      
      const configs = [];
      snapshot.forEach(docSnap => {
        configs.push({ id: docSnap.id, ...docSnap.data() });
      });
      setBoardConfigs(configs);
    }, err => {
      console.error('Error listening tableros:', err);
    });
    
    return () => unsub();
  }, [competitionId, isValidCompetition]);

  // *** RESTO DE FUNCIONES CON VALIDACIÓN ***
  const saveBoardConfig = useCallback(async (config) => {
    if (!competitionId || !isValidCompetition(competitionId)) throw new Error('No competitionId válido');
    
    if (config.id) {
      const ref = doc(db, 'competencias', competitionId, 'tableros', config.id);
      await setDoc(ref, config, { merge: true });
    } else {
      const col = collection(db, 'competencias', competitionId, 'tableros');
      const newDoc = await addDoc(col, config);
      return newDoc.id;
    }
  }, [competitionId, isValidCompetition]);

  const updateBoardConfig = useCallback(async (id, partial) => {
    if (!competitionId || !id || !isValidCompetition(competitionId)) return;
    const ref = doc(db, 'competencias', competitionId, 'tableros', id);
    await updateDoc(ref, partial);
  }, [competitionId, isValidCompetition]);

  const deleteBoardConfig = useCallback(async (id) => {
    if (!competitionId || !id || !isValidCompetition(competitionId)) return;
    const ref = doc(db, 'competencias', competitionId, 'tableros', id);
    await deleteDoc(ref);
  }, [competitionId, isValidCompetition]);

  const openTablero = useCallback((configId, heatDetails) => {
    if (!isValidCompetition(competitionId)) {
      console.error('[openTablero] Competencia no válida');
      return;
    }
    
    console.log('[openTablero] Iniciando con configId:', configId);
    console.log('[openTablero] heatDetails recibidos:', heatDetails);
    
    const cfg = boardConfigs.find(c => c.id === configId);
    if (!cfg) {
      console.error(`[openTablero] No se encontró configuración con id: ${configId}`);
      return;
    }

    console.log('[openTablero] Configuración encontrada:', cfg);

    if (!heatDetails) {
      console.error('[openTablero] No se recibieron datos del heat');
      return;
    }

    const payload = {
      ...cfg,
      eventData: {
        eventName: heatDetails.eventName || 'Sin nombre',
        eventSubtitle: heatDetails.eventSubtitle || '',
        eventNumber: heatDetails.eventNumber,
        round: heatDetails.round,
        heat: heatDetails.heat,
        eventCode: heatDetails.eventCode,
        participants: (heatDetails.participants || []).map(p => ({
          ...p,
          fullName: p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Sin nombre',
          idNumber: p.idNumber || '',
          affiliation: p.affiliation || '',
          lane: p.lane || '',
          time: p.time || '',
          place: p.place || '-',
          wind: p.wind || null,
          reactionTime: p.reactionTime || null,
          lapTimes: p.lapTimes || [],
          attempts: p.attempts || [],
          special: p.special || '-'
        }))
      },
      settings: {
        ...cfg.settings,
        assignedHeat: cfg.settings?.assignedHeat
      },
      testSettings: heatDetails.testSettings || {}
    };

    console.log('[openTablero] Payload completo construido:');
    console.log('- eventData presente:', !!payload.eventData);
    console.log('- eventData.participants:', payload.eventData?.participants?.length || 0);
    console.log('- displayRows final:', payload.settings?.displayRows);

    try {
      const configKey = `config-${configId}`;
      localStorage.setItem(configKey, JSON.stringify(payload));
      
      const saved = localStorage.getItem(configKey);
      const parsed = JSON.parse(saved);
      console.log('[openTablero] Verificación - eventData guardado:', !!parsed.eventData);
      console.log('[openTablero] Verificación - displayRows guardado:', parsed.settings?.displayRows);
      console.log('[openTablero] Datos guardados correctamente en localStorage');
    } catch (error) {
      console.error('[openTablero] Error al guardar en localStorage:', error);
      return;
    }

    const boardType = cfg.boardType || cfg.settings?.boardType || 'track';

    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const tableWidth = cfg.settings?.tableWidth || 1000;
    const tableHeight = cfg.settings?.tableHeight || 1200;
    const width = Math.min(tableWidth + 40, screenWidth);
    const height = Math.min(tableHeight + 40, screenHeight);

    const params = new URLSearchParams({
      id: configId,
      type: boardType,
      eventNumber: heatDetails.eventNumber || '',
      round: heatDetails.round || '',
      heat: heatDetails.heat || ''
    });

    const windowFeatures = [
      `width=${width}`,
      `height=${height}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=yes'
    ].join(',');

    const newWindow = window.open(
      `/scoreboard?${params.toString()}`,
      `tablero-${configId}`,
      windowFeatures
    );

    if (newWindow) {
      setOpenWindows(prev => ({ ...prev, [configId]: newWindow }));
      console.log('[openTablero] Ventana abierta exitosamente');
      
      setTimeout(() => {
        if (!newWindow.closed) {
          console.log('[openTablero] Enviando mensaje INITIAL_CONFIG a la ventana');
          newWindow.postMessage({
            type: 'INITIAL_CONFIG',
            config: payload
          }, window.location.origin);
        }
      }, 1000);
    } else {
      console.error('[openTablero] No se pudo abrir la ventana - verifica los popups bloqueados');
    }
  }, [boardConfigs, setOpenWindows, competitionId, isValidCompetition]);

  const refreshTablero = useCallback((configId, maybePayloadOrHeatDetails = null) => {
    if (!isValidCompetition(competitionId)) {
      console.error('[refreshTablero] Competencia no válida');
      return false;
    }
    
    const config = boardConfigs.find(c => c.id === configId);
    if (!config) {
      console.error('[refreshTablero] Configuración no encontrada:', configId);
      return false;
    }

    let payload;

    if (maybePayloadOrHeatDetails && maybePayloadOrHeatDetails.eventData) {
      payload = maybePayloadOrHeatDetails;
      console.log('[refreshTablero] Payload completo recibido directamente.');
    } else {
      const heatDetails = maybePayloadOrHeatDetails;

      payload = {
        ...config,
        eventData: heatDetails
          ? {
              eventName:     heatDetails.eventName     || 'Sin nombre',
              eventSubtitle: heatDetails.eventSubtitle || '',
              eventNumber:   heatDetails.eventNumber,
              round:         heatDetails.round,
              heat:          heatDetails.heat,
              eventCode:     heatDetails.eventCode,
              participants:  (heatDetails.participants || []).map(p => ({
                ...p,
                fullName:      p.fullName    || `${p.firstName||''} ${p.lastName||''}`.trim() || 'Sin nombre',
                idNumber:      p.idNumber    || '',
                affiliation:   p.affiliation || '',
                lane:          p.lane        || '',
                time:          p.time        || '',
                place:         p.place       || '-',
                wind:          p.wind        ?? null,
                reactionTime:  p.reactionTime|| null,
                lapTimes:      p.lapTimes    || [],
                attempts:      p.attempts    || [],
                special:       p.special     || '-',
                activeAthlete: null,
                displayMode: (config.settings?.displayRows === 1 ? 'individual' : 'multiple'),
              }))
            }
          : config.eventData,
        settings: {
          ...config.settings,
          assignedHeat: config.settings?.assignedHeat
        },
        testSettings: heatDetails?.testSettings || config.testSettings || {}
      };

      console.log('[refreshTablero] Payload reconstruido desde heatDetails:');
      console.log('- displayRows en config.settings:', payload.settings?.displayRows);
      console.log('- participants en eventData:', payload.eventData?.participants?.length);
    }

    try {
      localStorage.setItem(`config-${configId}`, JSON.stringify(payload));
      console.log('[refreshTablero] Guardado en localStorage. displayRows =', payload.settings?.displayRows);
    } catch (err) {
      console.error('[refreshTablero] Error escribiendo localStorage:', err);
    }

    const win = openWindows[configId];
    if (win && !win.closed) {
      try {
        win.postMessage({ type: 'CONFIG_UPDATE', config: payload }, window.location.origin);
        console.log('[refreshTablero] CONFIG_UPDATE enviado. displayRows =', payload.settings?.displayRows);
        return true;
      } catch (err) {
        console.error('[refreshTablero] Error enviando postMessage:', err);
        return false;
      }
    }

    console.log('[refreshTablero] No hay ventana abierta para actualizar');
    return false;
  }, [boardConfigs, openWindows, competitionId, isValidCompetition]);

  // *** RESTO DE FUNCIONES MANTENIENDO VALIDACIÓN ***
  const updateFileMetadata = useCallback(async (id, meta) => {
    if (!competitionId || !id || !meta || !isValidCompetition(competitionId)) return { success:false };
    
    const refMeta = doc(db,'competencias',competitionId,'syncedFiles',id);
    const payload = {
      url: meta.url,
      name: meta.name,
      timestamp: meta.timestamp instanceof Date
        ? Timestamp.fromDate(meta.timestamp)
        : meta.timestamp,
      originalPath: meta.originalPath || '',
      type: meta.type || '',
      isFieldEventFile: !!meta.isFieldEventFile
    };
    try {
      await setDoc(refMeta,payload,{ merge:true });
      setFileMetadata(prev => ({
        ...prev,
        [id]: {
          url: payload.url,
          name: payload.name,
          timestamp: payload.timestamp.toDate(),
          originalPath: payload.originalPath,
          type: payload.type,
          isFieldEventFile: payload.isFieldEventFile
        }
      }));
      return { success:true };
    } catch(e) {
      console.error("Error updateMeta:",e);
      if (isValidCompetition(competitionId)) {
        setError(`Err meta ${id}: ${e.message}`);
      }
      return { success:false,error:e };
    }
  },[competitionId, isValidCompetition]);

  const deleteFileMetadata = useCallback(async id => {
    if (!competitionId || !id || !isValidCompetition(competitionId)) return { success:false };
    
    const refMeta = doc(db,'competencias',competitionId,'syncedFiles',id);
    try {
      await deleteDoc(refMeta);
      setFileMetadata(prev => {
        const nxt = {...prev}; delete nxt[id]; return nxt;
      });
      return { success:true };
    } catch(e) {
      console.error("Error deleteMeta:",e);
      return { success:false,error:e };
    }
  },[competitionId, isValidCompetition]);

  const clearFieldFiles = useCallback(async () => {
    if (!isValidCompetition(competitionId)) return;
    
    const deletes = Object.entries(fileMetadata)
      .filter(([,m]) => m.isFieldEventFile)
      .map(([id]) => deleteDoc(doc(db,'competencias',competitionId,'syncedFiles',id)));
    await Promise.all(deletes);
    setFileMetadata(prev => {
      const nxt = {...prev};
      Object.entries(prev)
        .filter(([,m]) => m.isFieldEventFile)
        .forEach(([id]) => delete nxt[id]);
      return nxt;
    });
  },[competitionId,fileMetadata, isValidCompetition]);

  const isTrackEventFn           = useCallback(c => TrackEventCodes.includes(c), []);
  const isFieldHorizontalEventFn = useCallback(c => fieldHorizontalEvents.includes(c), []);
  const isFieldVerticalEventFn   = useCallback(c => fieldVerticalEvents.includes(c), []);

  const updateHeatParticipants = useCallback((en, ro, he, pid, lane, action = 'update', newAth) => {
    if (!isValidCompetition(competitionId)) return;
    
    setEvtData(prev => {
      const next = prev.map(e => {
        if (e.eventNumber === +en && e.round === +ro && e.heat === +he) {
          let parts = e.participants;
          if (action === 'update') {
            parts = parts.map(p => p.idNumber === pid
              ? { ...p, lane }
              : p
            );
          } else if (action === 'remove') {
            parts = parts.filter(p => p.idNumber !== pid);
          } else if (action === 'add' && newAth) {
            parts = [...parts, newAth];
          }
          return { ...e, participants: parts };
        }
        return e;
      });

      saveDataToSubcollection('evt', 'evtData', next);
      return next;
    });
  }, [competitionId, saveDataToSubcollection, isValidCompetition]);


  const updateParticipantResults = useCallback(
    async (en, ro, he, pid, res, keysToDelete = []) => {
      if (!isValidCompetition(competitionId)) return;
  
      const key = `${en}_${ro}_${he}`;
      console.log('🔄 Actualizando resultados participante:', { key, pid, keysToDelete });
  
      try {
        // 1) Actualizar estado local inmediatamente
        setParticipantsResults(prev => {
          const updated = { ...prev };
          if (!updated[key]) updated[key] = {};
          
          // Crear copia profunda para evitar mutaciones
          updated[key][pid] = JSON.parse(JSON.stringify(res));
          
          // Eliminar claves del estado local si se especifican
          if (keysToDelete.length > 0 && updated[key][pid].heights) {
            keysToDelete.forEach(k => {
              delete updated[key][pid].heights[k];
            });
          }
          
          return updated;
        });
  
        // 2) Preparar payload para Firestore
        const docRef = doc(db, 'competencias', competitionId, 'resultados', key);
        
        // Crear payload con datos serializados
        const payload = { 
          [pid]: JSON.parse(JSON.stringify(res))
        };
  
        // Agregar eliminaciones explícitas
        keysToDelete.forEach(k => {
          payload[`${pid}.heights.${k}`] = deleteField();
        });
  
        console.log('🔥 Enviando payload a Firestore:', payload);
  
        // 3) Usar updateDoc en lugar de merge para mayor control
        await setDoc(docRef, payload, { merge: true });
        
        console.log('✅ Resultados actualizados exitosamente en Firestore');
        
      } catch (error) {
        console.error('❌ Error actualizando resultados:', error);
        
        // En caso de error, intentar revertir estado local
        setParticipantsResults(prev => {
          // Aquí podrías implementar lógica de reversión más sofisticada
          return prev;
        });
        
        throw error;
      }
    },
    [competitionId, isValidCompetition]
  );
  
  // Función mejorada para updateHeatHeights
  const updateHeatHeights = useCallback(async (key, newArr) => {
    if (!isValidCompetition(competitionId)) return;
    
    console.log('🔄 Actualizando alturas para:', key, newArr);
    
    try {
      // 1) Actualizar estado local inmediatamente
      setHeatHeights(prev => ({ ...prev, [key]: newArr }));
  
      // 2) Persistir en Firestore usando setDoc para sobrescribir completamente
      const docRef = doc(db, 'competencias', competitionId, 'resultados', key);
      
      // Usar setDoc con merge: true pero especificando el campo heights
      await setDoc(docRef, { 
        heights: newArr.map(h => ({
          value: h.value,
          isTiebreak: h.isTiebreak || false
        }))
      }, { merge: true });
      
      console.log('✅ Alturas actualizadas exitosamente en Firestore:', key);
      
    } catch (error) {
      console.error('❌ Error actualizando alturas:', error);
      
      // Revertir cambio local en caso de error
      setHeatHeights(prev => {
        const reverted = { ...prev };
        // Intentar revertir al estado anterior
        return reverted;
      });
      
      throw error;
    }
  }, [competitionId, isValidCompetition]);

  const getParticipantResults = useCallback((en,ro,he,pid) => {
    return participantsResults[`${en}_${ro}_${he}`]?.[pid]||{};
  }, [participantsResults]);

  const updateHeatResults = useCallback((en,ro,he,pid,attNum,data) => {
    if (!isValidCompetition(competitionId)) return;
    
    const key = `${en}_${ro}_${he}`;
    const ev  = evtData.find(e => e.eventNumber===en && e.round===ro && e.heat===he);
    if (!ev) return;
    const track = isTrackEventFn(ev.eventCode);
    setParticipantsResults(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[key]) next[key] = {};
      if (!next[key][pid]) next[key][pid] = track
        ? { time:'', wind:undefined, special:'-' }
        : { attempts:[], special:'-' };
      const pr = next[key][pid];
      if (track) {
        if (attNum===0) {
          pr.time = data.time||'';
          pr.reactionTime = data.reactionTime ?? pr.reactionTime;
          pr.lapTimes     = data.lapTimes     || pr.lapTimes;  
          if (data.wind!==undefined) pr.wind = data.wind;
          pr.special = (data.special||'-').toUpperCase();
        }
      } else {
        if (!Array.isArray(pr.attempts)) pr.attempts = [];
        while(pr.attempts.length<attNum) pr.attempts.push(null);
        pr.attempts[attNum-1] = data;
        pr.special = (data.special||'-').toUpperCase();
      }
      return next;
    });
  }, [evtData, isTrackEventFn, competitionId, isValidCompetition]);

  const updateRankings = useCallback((key, data) => {
    if (!isValidCompetition(competitionId)) return;
    
    setRankings(prev => {
      const next = { ...prev, [key]: data };
      const rankRef = doc(db, 'competencias', competitionId, 'rankings', key);
      const cleanPayload = JSON.parse(JSON.stringify({ rankings: data }));
      queueWrite(rankRef, cleanPayload, { merge: true });
      return next;
    });
  }, [competitionId, queueWrite, isValidCompetition]);

  const calculateRankings = useCallback(async (en, ro, he) => {
    if (!isValidCompetition(competitionId)) return;
  
    const key = `${en}_${ro}_${he}`;
    if (!participantsResults[key] || isCalculating.current) return;
  
    isCalculating.current = true;
  
    try {
      /* ------------------------------------------------------------------ */
      /* 1. Datos base del heat                                             */
      /* ------------------------------------------------------------------ */
      const heatRes = participantsResults[key] || {};
      const ev      = evtData.find(
        e => e.eventNumber === en && e.round === ro && e.heat === he
      );
  
      if (!ev) { isCalculating.current = false; return; }
  
      const track  = TrackEventCodes.includes(ev.eventCode);
      const horiz  = fieldHorizontalEvents.includes(ev.eventCode);
      const vert   = fieldVerticalEvents.includes(ev.eventCode);
      const type   = track ? 'track' : horiz ? 'horizontal' : vert ? 'vertical' : 'unknown';
  
      const { athletesInFinal = 0 } = testSettingsMap[`${en}_${ro}`] || {};
  
      // Alturas para verticales
      const heightsArr = heatHeights[key] || [];
  
      /* ------------------------------------------------------------------ */
      /* 2. Construir participantes con TODOS sus resultados                */
      /* ------------------------------------------------------------------ */
      const participantsWithResults = ev.participants.map(p => {
        const baseResults = heatRes[p.idNumber] || {};
        return { ...p, results: baseResults };          // <— ¡se conserva p.results!
      });
  
      /* ------------------------------------------------------------------ */
      /* 3. Calcular bestMark y empaquetar para assignPlaces                */
      /* ------------------------------------------------------------------ */
      const participantsForPlaces = participantsWithResults.map(p => {
        const bestMark = type === 'vertical'
          ? calculateBestMark(p.idNumber, p.results, type)
          : calculateBestMark(p.idNumber, p.results, type);
  
        return {
          ...p,                     // lane, nombre, etc.
          bestMark,
          special : (p.results.specialStatus ?? p.results.special ?? '-'),
          attempts: p.results.attempts || [],
        };
      });
  
      console.log(`[calculateRankings] Heat ${key} – bestMark:`,
        participantsForPlaces.map(({ idNumber, bestMark }) => ({ idNumber, bestMark }))
      );
  
      /* ------------------------------------------------------------------ */
      /* 4. Lugares (con desempates).  Para verticales se envían heightsArr */
      /* ------------------------------------------------------------------ */
      const calculatedPlaces = assignPlaces(
        participantsForPlaces,
        type,
        false,                 // isPreliminary
        athletesInFinal,
        heightsArr             // <- usado solo en verticales
      );
  
      console.log(`[calculateRankings] Heat ${key} – places:`, calculatedPlaces);
  
      /* ------------------------------------------------------------------ */
      /* 5. Detectar cambios y escribir                                     */
      /* ------------------------------------------------------------------ */
      const updates = {};
      participantsWithResults.forEach(p => {
        const newPlace  = calculatedPlaces[p.idNumber]?.place?.toString() || '-';
        const newPassed = !!calculatedPlaces[p.idNumber]?.passed;
        const current   = heatRes[p.idNumber] || {};
  
        if (current.place !== newPlace || current.passed !== newPassed) {
          updates[p.idNumber] = {
            ...current,
            place : newPlace,
            passed: newPassed,
          };
        }
      });
  
      if (Object.keys(updates).length) {
        console.log(`[calculateRankings] Heat ${key} – actualizando:`, updates);
  
        // Estado local
        setParticipantsResults(prev => ({
          ...prev,
          [key]: { ...prev[key], ...updates },
        }));
  
        // Firestore
        const batch  = writeBatch(db);
        const resRef = doc(db, 'competencias', competitionId, 'resultados', key);
        batch.set(resRef, updates, { merge: true });
        batch.commit().catch(err => console.error('batch set (merge)', err));
      }
    } finally {
      // Pequeño “cool-down” para evitar recalculos en cascada
      setTimeout(() => { isCalculating.current = false; }, 300);
    }
  }, [
    competitionId,
    evtData,
    participantsResults,
    testSettingsMap,
    heatHeights,
    isValidCompetition,
  ]);

  const throttledCalculate = useMemo(
    () => throttle(calculateRankings, 300, { 
      leading: true, 
      trailing: true,
      maxWait: 1000
    }),
    [calculateRankings]
  );

  useEffect(() => {
    return () => {
      throttledCalculate.cancel();
    };
  }, [throttledCalculate]);

  useEffect(() => {
    if (!evtData.length || !isValidCompetition(competitionId)) return;
    
    const calculationQueue = [];
    Object.keys(participantsResults).forEach(key => {
      const [en, ro, he] = key.split('_').map(Number);
      calculationQueue.push(() => throttledCalculate(en, ro, he));
    });

    calculationQueue.forEach((fn, i) => setTimeout(fn, i * 300));
  }, [participantsResults, evtData, throttledCalculate, competitionId, isValidCompetition]);

  const getPlace = useCallback((en, ro, he, pid) => {
    const key = `${en}_${ro}_${he}`;
    return participantsResults[key]?.[pid]?.place ?? '-';
  }, [participantsResults]);
  
  const hasPassed = useCallback((en, ro, he, pid) => {
    const key = `${en}_${ro}_${he}`;
    return participantsResults[key]?.[pid]?.passed ?? false;
  }, [participantsResults]);

  const updateHeatWind = useCallback((en, ro, he, windValue) => {
    if (!isValidCompetition(competitionId)) return;
    
    const key    = `${en}_${ro}_${he}`;
    const docRef = doc(db, 'competencias', competitionId, 'resultados', key);

    setParticipantsResults(prev => {
      const next = { ...prev, [key]: { ...(prev[key] || {}) } };

      evtData
        .find(e => e.eventNumber===en && e.round===ro && e.heat===he)
        ?.participants.forEach(p => {
          const id = p.idNumber.toString();
          const current = next[key][id] || {};
          next[key][id] = { ...current, wind: windValue };
        });

      queueWrite(docRef, next[key], { merge:true });
      return next;
    });
  }, [competitionId, queueWrite, evtData, isValidCompetition]);

  const updateRowPrecision = useCallback((heatKey, athleteId, precisionValue) => {
    if (!isValidCompetition(competitionId)) return;
    
    setRowPrecisions(prev => ({
      ...prev,
      [heatKey]: {
        ...(prev[heatKey] || {}),
        [athleteId]: precisionValue
      }
    }));

    const resRef = doc(db, 'competencias', competitionId, 'resultados', heatKey);
    const existing = participantsResults[heatKey]?.[athleteId] || {};

    const updatedAthlete = {
      ...existing,
      precision: precisionValue
    };

    if (typeof updatedAthlete.timeRaw === 'number') {
      updatedAthlete.time = formatWithPrecision(
        updatedAthlete.timeRaw,
        precisionValue
      );
    }

    queueWrite(
      resRef,
      { [athleteId]: updatedAthlete },
      { merge: true }
    );
  }, [competitionId, queueWrite, participantsResults, isValidCompetition]);

  useEffect(() => {
    if (!loading && evtData.length && Object.keys(participantsResults).length && isValidCompetition(competitionId)) {
      Object.keys(participantsResults).forEach(key => {
        const [en,ro,he] = key.split('_').map(Number);
        calculateRankings(en,ro,he);
      });
    }
  }, [participantsResults, evtData, calculateRankings, loading, competitionId, isValidCompetition]);

  const updateTestSettings = useCallback(
    async (eventNumber, round, heat, settings) => {
      if (!competitionId || !isValidCompetition(competitionId)) return;

      const docId  = `${eventNumber}_${round}`;
      const refDoc = doc(db, 'competencias', competitionId, 'testSettings', docId);
      await setDoc(refDoc, settings, { merge: true });

      setTestSettingsMap(prev => ({
        ...prev,
        [docId]: {
          ...(prev[docId] || {}),
          ...settings
        }
      }));

      return settings;
    },
    [competitionId, setTestSettingsMap, isValidCompetition]
  );

  const applyAdvancementFormula = useCallback((eventNumber, round, directSettings = null) => {
    if (!isValidCompetition(competitionId)) return;
    
    const heats = evtData.filter(e =>
      +e.eventNumber === +eventNumber && +e.round === +round);

    if (!heats.length) return;

    const settings = directSettings || getTestSettings(eventNumber, round);
    const { advancementType, advancementDetail, lanesCount } = settings;
    
    if (advancementType !== 'posicion' || !advancementDetail) return;

    const [topPerHeat, bestTimes] = advancementDetail.split('-').map(Number);

    const byHeatQ = {};
    const globalTimes = [];

    heats.forEach(h => {
      const key = `${h.eventNumber}_${h.round}_${h.heat}`;
      const heatMap = participantsResults[key] || {};
      const heatRanks = Object.entries(heatMap)
        .map(([id, res]) => ({ id, place: res.place || '-' }))
        .filter(r => !isNaN(+r.place));
      const resMap = participantsResults[key] || {};

      const sorted = heatRanks
        .filter(r => !isNaN(+r.place))
        .sort((a,b) => +a.place - +b.place);

      byHeatQ[h.heat] = new Set();
      sorted.slice(0, topPerHeat).forEach(r => byHeatQ[h.heat].add(r.id));

      sorted.forEach(r => {
        const pr = resMap[r.id] || {};
        const timeRaw = pr.timeRaw ?? parseTime(pr.time || '');
        if (isNaN(timeRaw)) return;
        globalTimes.push({ id: r.id, heat: h.heat, timeRaw });
      });
    });

    const qCandidates = globalTimes.filter(t => !byHeatQ[t.heat].has(t.id));
    const track = TrackEventCodes.includes(heats[0].eventCode);
    qCandidates.sort((a,b) => track ? a.timeRaw - b.timeRaw : b.timeRaw - a.timeRaw);
    const qSelected = qCandidates.slice(0, bestTimes).map(t => t.id);

    const writes = {};

    heats.forEach(h => {
      const key = `${h.eventNumber}_${h.round}_${h.heat}`;
      const resCopy = { ...(participantsResults[key]||{}) };

      Object.keys(resCopy).forEach(pid => {
        let qualifier = resCopy[pid].qualifier || '';
        if (byHeatQ[h.heat].has(pid))      qualifier = 'Q';
        else if (qSelected.includes(pid))  qualifier = 'q';
        else                               qualifier = '';
        resCopy[pid] = { ...resCopy[pid], qualifier };
      });
      writes[key] = resCopy;
    });

    setParticipantsResults(prev => ({ ...prev, ...writes }));

    return Promise.all(Object.entries(writes).map(([key, map]) => {
      const ref = doc(db, 'competencias', competitionId, 'resultados', key);
      return queueWrite(ref, JSON.parse(JSON.stringify(map)), { merge:true });
    }));

  }, [
    evtData, rankings, participantsResults,
    competitionId, queueWrite, isValidCompetition
  ]);

  const testSettings = useMemo(() => {
    const ts = {};
    evtData.forEach(({ eventNumber, round, heat }) => {
      ts[`${eventNumber}_${round}_${heat}`] =
        getTestSettings(eventNumber, round);
    });
    return ts;
  }, [evtData, getTestSettings]);

  const saveSeedMarks = useCallback(async (idNumber, eventCode, seedMark) => {
    if (!competitionId || !isValidCompetition(competitionId)) throw new Error('No competitionId válido');
    
    try {
      const seedMarkRef = doc(db, 'competencias', competitionId, 'seedMarks', idNumber);
      const seedMarkDoc = await getDoc(seedMarkRef);
      
      const updateData = {};
      updateData[`${eventCode}`] = {
        seedMark: seedMark,
        updatedAt: Timestamp.now()
      };
      
      if (!seedMarkDoc.exists()) {
        updateData.idNumber = idNumber;
        updateData.createdAt = Timestamp.now();
      }
      
      await setDoc(seedMarkRef, updateData, { merge: true });
      
      console.log(`Marca de siembra guardada para atleta ${idNumber} en evento ${eventCode}: ${seedMark}`);
      return true;
    } catch (error) {
      console.error('Error al guardar marca de siembra:', error);
      throw error;
    }
  }, [competitionId, db, isValidCompetition]);

  const importSeedMarks = useCallback(async (seedMarksArray) => {
    if (!competitionId || !isValidCompetition(competitionId)) throw new Error('No competitionId válido');
    
    if (!Array.isArray(seedMarksArray)) {
      throw new Error('seedMarksArray debe ser un arreglo');
    }
    
    try {
      const batch = writeBatch(db);
      
      seedMarksArray.forEach(mark => {
        const { idNumber, eventCode, seedMark, seasonBest, personalBest } = mark;
        
        if (!idNumber || !eventCode || !seedMark) {
          console.error(`Faltan campos requeridos en la marca: ${JSON.stringify(mark)}`);
          return;
        }
        
        const ref = doc(db, 'competencias', competitionId, 'seedMarks', idNumber);
        
        const updateData = {
          idNumber: idNumber,
          seedMark: seedMark,
          updatedAt: Timestamp.now(),
          eventCode: eventCode
        };
        
        if (seasonBest) updateData.seasonBest = seasonBest;
        if (personalBest) updateData.personalBest = personalBest;
        
        batch.set(ref, updateData, { merge: true });
      });
      
      await batch.commit();
      console.log(`${seedMarksArray.length} marcas de siembra importadas correctamente`);
      return true;
    } catch (error) {
      console.error('Error al importar marcas de siembra:', error);
      throw error;
    }
  }, [competitionId, db, isValidCompetition]);

  const getSeedMarks = useCallback(async (idNumber) => {
    if (!competitionId || !isValidCompetition(competitionId)) return null;
    
    try {
      const seedMarkRef = doc(db, 'competencias', competitionId, 'seedMarks', idNumber);
      const seedMarkDoc = await getDoc(seedMarkRef);
      
      if (!seedMarkDoc.exists()) {
        return null;
      }
      
      return seedMarkDoc.data();
    } catch (error) {
      console.error('Error al obtener marcas de siembra:', error);
      return null;
    }
  }, [competitionId, db, isValidCompetition]);

  const getHeatWind = useCallback((eventNumber, round, heat) => {
    const eventData = evtData.find(
      e => e.eventNumber === parseInt(eventNumber, 10) && 
           e.round === parseInt(round, 10) && 
           e.heat === parseInt(heat, 10)
    );
    
    return eventData?.wind || '';
  }, [evtData]);

  const getHeatImages = useCallback(
    async (en, ro, he) => {
      if (!competitionId || !userId || !isValidCompetition(competitionId)) throw new Error('Faltan parámetros válidos');

      const eventStr = en.toString();
      const roundStr = ro.toString();
      const heatStr  = he.toString().padStart(2, '0');

      const filePrefix = `${eventStr}-${roundStr}-${heatStr}`;  
      const folderPath = `usuarios/${userId}/competencias/${competitionId}/Fotofinish`;
      console.log('[getHeatImages] looking in →', folderPath);
      console.log('[getHeatImages] filtering prefix →', filePrefix);

      const folderRef = storageRef(storage, folderPath);
      try {
        const { items } = await listAll(folderRef);
        console.log('[getHeatImages] all names →', items.map(i => i.name));

        const matched = items.filter(i => i.name.startsWith(filePrefix));
        console.log('[getHeatImages] matched →', matched.map(i => i.name));

        const urls = await Promise.all(matched.map(i => getDownloadURL(i)));
        console.log('[getHeatImages] URLs →', urls);
        return urls;
      } catch (err) {
        console.error('[getHeatImages] error:', err);
        return [];
      }
    },
    [competitionId, userId, isValidCompetition]
  );

  const isHeatComplete = useCallback(
    (heatDetails) => {
      if (!heatDetails) return false;
  
      const { eventNumber, round, heat, eventCode, participants } = heatDetails;
      const key       = `${eventNumber}_${round}_${heat}`;
      const heatMap   = participantsResults[key] || {};
      const testSet   = getTestSettings(eventNumber, round);
  
      const type = TrackEventCodes.includes(eventCode)
        ? 'track'
        : fieldHorizontalEvents.includes(eventCode)
          ? 'horizontal'
          : fieldVerticalEvents.includes(eventCode)
            ? 'vertical'
            : 'unknown';
  
      /** Recorremos TODOS los atletas inscritos */
      return participants.every(at => {
        const res = heatMap[at.idNumber] || {};
  
        /* 1 ▪ Pista */
        if (type === 'track') {
          return !!res.time || (res.special && res.special !== '-');
        }
  
        /* 2 ▪ Horizontales */
        if (type === 'horizontal') {
          const validAttempt = (res.attempts || []).some(a => {
            const v = parseFloat(a?.result);
            return !isNaN(v);
          });
          return validAttempt || (res.special && res.special !== '-');
        }
  
        /* 3 ▪ Verticales */
        if (type === 'vertical') {
          const heightsObj = res.heights || {};
          const hasO = Object.values(heightsObj)
            .some(h => (h.attempts || []).includes('O'));
  
          return (res.specialStatus && res.specialStatus !== 'Valid') || hasO;
        }
  
        /* 4 ▪ Tipo no reconocido ⇒ nunca completo */
        return false;
      });
    },
    [participantsResults, getTestSettings]
  );

  /* -------- setHeatStatus -------- */
  const setHeatStatus = useCallback(async (en, ro, he, status) => {
    if (!competitionId) return;

    const key    = `${en}_${ro}_${he}`;
    const refDoc = doc(db, 'competencias', competitionId, 'resultados', key);

    const payload = { status };
    if (status === 'in_progress') payload.startTime = nowTS();
    if (status === 'finished')    payload.endTime   = nowTS();

    // 1️⃣  feedback inmediato en el estado local
    setParticipantsResults(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), status, ...payload }
    }));

    // 2️⃣  persistir en Firestore
    await setDoc(refDoc, payload, { merge: true });
  }, [competitionId]);

  const setResultStatus = useCallback(
      async (en, ro, he, newStatus) => {
        if (!isValidCompetition(competitionId)) return;

        const key = `${en}_${ro}_${he}`;
        const ref = doc(
          db,
          'competencias',
          competitionId,
          'resultados',
          key
        );

        // 1️⃣ feedback inmediato
        setParticipantsResults(prev => ({
          ...prev,
          [key]: { ...(prev[key] || {}), resultStatus: newStatus }
        }));

        // 2️⃣ persistir en Firestore
        await setDoc(ref, { resultStatus: newStatus }, { merge: true });
      },
      [competitionId, isValidCompetition]
    );

  const value = useMemo(() => ({
    competitionId,
    evtData, setEvtData,
    pplData, setPplData,
    schData, setSchData,
    participantsResults,
    updateParticipantResults,
    getParticipantResults,
    updateHeatResults,
    updateHeatParticipants,
    rankings,
    updateRankings,
    calculateRankings,
    testSettingsMap,
    updateTestSettings,
    applyAdvancementFormula,
    getTestSettings,
    testSettings,
    loading, error, setError,
    manualUpload, setManualUpload,
    fileMetadata,
    updateFileMetadata,
    deleteFileMetadata,
    clearFieldFiles,
    refreshFiles,
    isTrackEventFn,
    isFieldHorizontalEventFn,
    isFieldVerticalEventFn,
    updateHeatHeights,
    heatHeights, buildHeightMap,
    parseTime, formatTime,
    TrackEventCodes, fieldHorizontalEvents, fieldVerticalEvents,
    calculateBestMark, assignPlaces, calculateFinalists,
    precision,
    setPrecision:updatePrecision,
    updateRowPrecision,
    rowPrecisions,
    updateHeatWind,
    clearQualifiers,
    saveSeedMarks,
    importSeedMarks,
    getSeedMarks,
    getHeatWind,
    getPlace,
    hasPassed,
    getHeatImages,
    boardConfigs,
    saveBoardConfig,
    updateBoardConfig,
    deleteBoardConfig,
    openTablero,
    refreshTablero,
    isHeatComplete,
    setHeatStatus,
    setResultStatus,
  }), [
    competitionId,
    evtData, pplData, schData,
    participantsResults, rankings,
    testSettings,
    loading, error, manualUpload,
    fileMetadata,
    precision, updatePrecision,
    rowPrecisions, updateRowPrecision,
    updateParticipantResults,
    getParticipantResults,
    updateHeatResults,
    updateRankings,
    updateFileMetadata,
    deleteFileMetadata,
    clearFieldFiles,
    refreshFiles,
    getPlace,
    hasPassed,
    boardConfigs,
    saveBoardConfig,
    updateBoardConfig,
    deleteBoardConfig,
    openTablero,
    refreshTablero,
    isValidCompetition,
  ]);

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

EventProvider.propTypes = {
  children: PropTypes.node.isRequired,
  competitionId: PropTypes.string
};

export const useEventContext = () => {
  const ctx = useContext(EventContext);
  if (!ctx) throw new Error('useEventContext debe usarse dentro de EventProvider');
  return ctx;
};